import { useEffect, useState } from "react";
import { api, eur } from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const now = new Date();
const y = now.getFullYear();
const m = now.getMonth() + 1;
const prevMonth = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
const curMonth = `${y}-${String(m).padStart(2, "0")}`;

export default function Auswertungen() {
  const [year, setYear] = useState(y);
  const [yearData, setYearData] = useState([]);
  const [compare, setCompare] = useState(null);
  const [mA, setMA] = useState(prevMonth);
  const [mB, setMB] = useState(curMonth);
  const [top, setTop] = useState([]);

  useEffect(() => {
    api.get(`/stats/year?year=${year}`).then((r) => setYearData(r.data));
    api.get("/stats/top-products").then((r) => setTop(r.data));
  }, [year]);

  useEffect(() => {
    api.get(`/stats/compare?a=${mA}&b=${mB}`).then((r) => setCompare(r.data));
  }, [mA, mB]);

  const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const chart = yearData.map((d) => ({ ...d, name: MONTHS[parseInt(d.month.slice(5)) - 1] }));

  return (
    <div className="space-y-8" data-testid="auswertungen-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Analyse</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Auswertungen</h1>
        </div>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="year-select" className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
          {[y, y - 1, y - 2].map((yy) => <option key={yy} value={yy}>{yy}</option>)}
        </select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-display font-medium mb-4">Jahresübersicht {year} · Einkauf / Verkauf / Gewinn</h3>
        <div className="h-64" data-testid="year-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} />
              <YAxis tick={{ fontSize: 11, fill: "#71717A" }} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Bar dataKey="einkauf" name="Einkauf" fill="#D4D4D8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="verkauf" name="Verkauf" fill="#18181B" radius={[3, 3, 0, 0]} />
              <Bar dataKey="gewinn" name="Gewinn" fill="#71717A" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm" data-testid="month-compare">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <h3 className="font-display font-medium">Monatsvergleich</h3>
          <input type="month" value={mA} onChange={(e) => setMA(e.target.value)} data-testid="compare-month-a" className="border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" />
          <span className="text-zinc-400 text-sm">vs.</span>
          <input type="month" value={mB} onChange={(e) => setMB(e.target.value)} data-testid="compare-month-b" className="border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" />
        </div>
        {compare && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead><tr className="border-b border-zinc-100 text-left">
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Kennzahl</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">{mA}</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">{mB}</th>
                <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Veränderung</th>
              </tr></thead>
              <tbody className="divide-y divide-zinc-50">
                {[["Ankäufe", compare.a.einkauf.anzahl, compare.b.einkauf.anzahl, compare.delta.ankaeufe, false],
                  ["Verkäufe", compare.a.verkauf.anzahl, compare.b.verkauf.anzahl, compare.delta.verkaeufe, false],
                  ["Umsatz", compare.a.ergebnis.umsatz, compare.b.ergebnis.umsatz, compare.delta.umsatz, true],
                  ["Einkauf", compare.a.einkauf.wert, compare.b.einkauf.wert, null, true],
                  ["Gewinn", compare.a.ergebnis.gewinn, compare.b.ergebnis.gewinn, compare.delta.gewinn, true],
                  ["Marge", `${compare.a.ergebnis.marge} %`, `${compare.b.ergebnis.marge} %`, compare.delta.marge, false]].map(([label, va, vb, d, isMoney]) => (
                  <tr key={label}>
                    <td className="py-2.5 text-zinc-600">{label}</td>
                    <td className="py-2.5 text-right">{isMoney ? eur(va) : va}</td>
                    <td className="py-2.5 text-right font-medium">{isMoney ? eur(vb) : vb}</td>
                    <td className={`py-2.5 text-right font-mono text-xs ${d == null ? "text-zinc-300" : d >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {d == null ? "–" : `${d >= 0 ? "+" : ""}${d} %`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm" data-testid="top-products">
        <h3 className="font-display font-medium mb-4">Top-Produkte</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="border-b border-zinc-100 text-left">
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400">#</th>
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Produkt</th>
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Verkauft</th>
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Ø Einkauf</th>
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Ø Verkauf</th>
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Ø Gewinn</th>
              <th className="py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Ø Lagerdauer</th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-50">
              {top.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-zinc-400">Noch keine verkauften Artikel.</td></tr>}
              {top.map((p, i) => (
                <tr key={p.name}>
                  <td className="py-2.5 font-mono text-zinc-400">{i + 1}</td>
                  <td className="py-2.5 font-medium">{p.name}</td>
                  <td className="py-2.5 text-right">{p.count}</td>
                  <td className="py-2.5 text-right">{eur(p.avg_purchase)}</td>
                  <td className="py-2.5 text-right">{eur(p.avg_sale)}</td>
                  <td className="py-2.5 text-right">{eur(p.avg_profit)}</td>
                  <td className="py-2.5 text-right">{p.avg_duration} Tage</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
